// Pure helpers for Evidence Chat: citation markers, page anchors, file resolution
// and @-mentions (ported from FRaiME's ProjectChat.tsx). The text-layer search
// that finds a cited passage inside a PDF lives in @lumanity/ui/viewer.

/** Anything with an id and a filename — an EvidenceDocument fits. */
export interface NamedDocument {
  id: string;
  filename: string;
}

/** Inline citation markers [N], [N, M]. Must match the backend's filter. */
export const CITATION_MARKER_RE = /\[(\d+(?:\s*,\s*\d+)*)\]/g;

/** Link prefix marking citation links inside the rendered markdown. */
export const CITATION_HREF_PREFIX = "#cite-";

/**
 * content-intelligence stores `sourcepage` as a bare number or "<file>#page=N".
 * Returns the page, or null when there is none.
 */
export function extractPageNumber(sourcepage: string | undefined | null): number | null {
  if (!sourcepage) return null;
  const fragment = /#page=(\d+)/.exec(sourcepage);
  if (fragment) return Number.parseInt(fragment[1], 10);
  const bare = /^\s*(\d+)\s*$/.exec(sourcepage);
  return bare ? Number.parseInt(bare[1], 10) : null;
}

/** Turn [1, 2] markers into markdown links ([1](#cite-1)[2](#cite-2)) the renderer swaps for buttons. */
export function linkifyCitations(text: string): string {
  return text.replace(CITATION_MARKER_RE, (_match, group: string) =>
    group
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
      .map((id) => `[${id}](${CITATION_HREF_PREFIX}${id})`)
      .join(""),
  );
}

function baseName(name: string): string {
  return (name.split(/[/\\]/).pop() ?? "").toLowerCase();
}

/**
 * The project document a citation's sourcefile refers to, tolerating a path
 * prefix or different casing between the indexed blob name and the evidence-base filename.
 */
export function findDocBySourcefile<T extends NamedDocument>(sourcefile: string, documentsByFilename: Map<string, T>): T | undefined {
  const direct = documentsByFilename.get(sourcefile);
  if (direct) return direct;
  const wanted = baseName(sourcefile);
  if (!wanted) return undefined;
  for (const [key, doc] of documentsByFilename.entries()) {
    if (baseName(key) === wanted) return doc;
  }
  return undefined;
}

/**
 * Document ids for the @filename mentions in a message, when there is at least
 * one and every one resolves to a project document; otherwise null (don't narrow the scope).
 */
export function parseAtMentionIds<T extends NamedDocument>(text: string, documentsByFilename: Map<string, T>): string[] | null {
  const mentions = Array.from(text.matchAll(/@(\S+)/g), (m) => m[1]);
  if (mentions.length === 0) return null;
  const ids: string[] = [];
  for (const mention of mentions) {
    const doc = findDocBySourcefile(mention, documentsByFilename);
    if (!doc) return null;
    ids.push(doc.id);
  }
  return [...new Set(ids)];
}

/** The fragment after "@" when the cursor sits inside an @-token (no whitespace since the @); else null. */
export function getAtQuery(text: string, cursor: number): string | null {
  const before = text.slice(0, cursor);
  const atIdx = before.lastIndexOf("@");
  if (atIdx === -1) return null;
  const fragment = before.slice(atIdx + 1);
  if (/\s/.test(fragment)) return null;
  return fragment;
}

// Compact relative age for the session list ("3h ago").
const RELATIVE_TIME_UNITS = [
  { upto: 60, per: 1, label: "s" },
  { upto: 3600, per: 60, label: "m" },
  { upto: 86400, per: 3600, label: "h" },
  { upto: 604800, per: 86400, label: "d" },
  { upto: 2629800, per: 604800, label: "w" },
  { upto: 31557600, per: 2629800, label: "mo" },
  { upto: Infinity, per: 31557600, label: "y" },
];

export function formatRelativeAge(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSeconds = Math.max(0, (now - then) / 1000);
  const unit = RELATIVE_TIME_UNITS.find(({ upto }) => diffSeconds < upto) ?? RELATIVE_TIME_UNITS[RELATIVE_TIME_UNITS.length - 1];
  if (unit.label === "s") return "just now";
  return `${Math.floor(diffSeconds / unit.per)}${unit.label} ago`;
}
